import { getApiBaseUrl } from './api';
import OidcAuthService from './oidcAuth';
import type { ProductDisplayData, ProductItem } from '../components/products/types';
import type {
  CollaboratorGarmentLane,
  CollaboratorProspect,
} from '../types/collaboratorProspect';

export interface CollaboratorBoosts {
  only_unifirst?: boolean;
  only_catalogue?: boolean;
  only_personalizable?: boolean;
  only_fr?: boolean;
  only_hi_vis?: boolean;
  boost_made_by_unifirst?: boolean;
  boost_in_catalogue?: boolean;
  boost_personalizable?: boolean;
  boost_fr_rated?: boolean;
  boost_hi_visibility?: boolean;
  penalty_vendors?: string[];
  penalty_brands?: string[];
}

export interface CollaboratorFilterTagResolution {
  field?: string;
  value?: unknown;
  match_type?: string;
}

export interface CollaboratorFilterTag {
  label: string;
  field: string;
  value?: string;
  resolved?: CollaboratorFilterTagResolution[];
}

export interface CollaboratorFilterAgentDebug {
  hard_filters?: Array<Record<string, unknown>>;
  pattern_filters?: Array<Record<string, unknown>>;
  soft_filters?: string[];
}

/**
 * A free-text constraint the backend resolved to a REAL catalog filter (above
 * the semantic-match threshold). Surfaced as a confirm-first "Apply as filter"
 * chip; applying it adds a draggable filter node + the pre-resolved OData.
 */
export interface CollaboratorConstraintFilter {
  term: string;
  field?: string;
  value?: unknown;
  label: string;
  /** 0..1 semantic-match confidence (1.0 for exact resolutions). */
  confidence: number;
  /** Ready-to-apply OData clause for this single constraint. */
  odata: string;
  tag?: CollaboratorFilterTag | null;
}

export type CollaboratorChatMode = 'discuss' | 'filter' | 'search' | 'answer' | 'mixed';

/** One bookmarked product as carried in the focus-node context. */
export interface CollaboratorContextProduct {
  sku: string;
  name?: string;
  class_description?: string;
  sub_class?: string;
  std_cost?: number;
  variant_count?: number;
}

/** Compact rollup metrics for a graph node (own + inherited). */
export interface CollaboratorNodeMetrics {
  num_products?: number;
  num_items?: number;
  est_cost?: number;
}

/** Summary of a non-focused node the agent should be aware of. */
export interface CollaboratorNodeSummary extends CollaboratorNodeMetrics {
  id: string;
  label: string;
  kind: string;
  status?: string;
  /** Qualitative requirements maintained on this node. */
  wants?: string[];
  constraints?: string[];
}

/** Full detail for the node the user is currently focused on. */
export interface CollaboratorFocusNode extends CollaboratorNodeMetrics {
  id: string;
  label: string;
  kind: string;
  class_description?: string;
  products?: CollaboratorContextProduct[];
  /** Wants/constraints set directly on the focused node. */
  wants?: string[];
  constraints?: string[];
  /** Wants/constraints inherited from ancestor nodes (read-only here). */
  inherited_wants?: string[];
  inherited_constraints?: string[];
}

export interface CollaboratorChatContext {
  topic?: string;
  phase?: 'explore' | 'results' | 'pending_inquire';
  has_results?: boolean;
  product_count?: number;
  /** Node the user is focused on — agent sees full product detail here. */
  focus_node?: CollaboratorFocusNode;
  /** Other inquiry nodes — agent sees summaries (counts + cost + status). */
  other_nodes?: CollaboratorNodeSummary[];
  /** Conversation turns scoped to focus node + its ancestors. */
  history?: Array<{ role: 'user' | 'agent'; content: string }>;
}

export interface CollaboratorSuggestedAction {
  type: 'filter' | 'category' | 'industry' | 'search' | 'note' | 'focus';
  label: string;
  terms?: string[];
  search_query?: string;
  /** For `focus` actions — id or label of the node to focus. */
  node_id?: string;
  node_label?: string;
  /**
   * For `category` actions — product categories the agent offers and the
   * backend pre-resolved into a ready-to-apply OR filter (`filters` OData) plus
   * a single removable chip (`tag`). `level` selects the hierarchy field:
   * "class" → `class_description` (default), "subclass" → `sub_class_description`.
   */
  categories?: string[];
  level?: 'class' | 'subclass';
  filters?: string;
  tag?: CollaboratorFilterTag;
  /**
   * For `filter` actions — the backend pre-validated the proposed terms through
   * the threshold resolver. Only terms that resolve to real catalog filters
   * survive; `tags` are the removable chips and `resolved` carries per-term
   * OData/confidence. Absent/empty means the action was dropped server-side.
   */
  tags?: CollaboratorFilterTag[];
  resolved?: CollaboratorConstraintFilter[];
  /**
   * For `industry` actions — approved industries (OR-combined) the backend
   * pre-resolved into an `industry` ismatch filter (`filters`) + chip (`tag`),
   * plus the merged Industry Top-10 style codes that light the gold badges.
   */
  industries?: string[];
  industryTop10Codes?: string[];
  industryName?: string;
}

export interface CollaboratorFilterResponse {
  success: boolean;
  mode?: CollaboratorChatMode;
  search_query?: string;
  terms?: string[];
  boosts?: CollaboratorBoosts;
  filters?: string;
  filter_agent?: CollaboratorFilterAgentDebug;
  reply?: string;
  tags?: CollaboratorFilterTag[];
  suggested_actions?: CollaboratorSuggestedAction[];
  /** Agent-initiated focus switch — frontend should focus this node. */
  set_focus?: { node_id?: string; node_label?: string };
  /**
   * Progressive qualitative summary the agent maintains for the focused node.
   * When present, the frontend REPLACES the focused node's wants/constraints
   * with these lists (lets the agent relax/delete on contradiction).
   */
  node_summary?: { wants?: string[]; constraints?: string[] } | null;
  /**
   * Constraints ADDED this turn that resolved to real catalog filters above
   * threshold. The frontend surfaces these as confirm-first "Apply as filter"
   * chips — applying adds a draggable filter node scoped to the focused node.
   */
  constraint_filters?: CollaboratorConstraintFilter[];
  /**
   * Prospect fields the agent extracted from a natural-language intro. When
   * present (and no customer root exists yet), the frontend bootstraps the
   * inquiry from these fields.
   */
  prospect?: Partial<CollaboratorProspect> | null;
  /**
   * When a typed utterance applies an industry filter, the merged Industry
   * Top-10 style codes (and a display name) so the frontend can light the gold
   * badges for every chosen industry. Empty when no industry was applied.
   */
  industry_top_10_codes?: string[];
  industry_name?: string;
  error?: string;
}

/**
 * A quick, token-light category-filter suggestion the backend derived by
 * semantic-matching the query against the product category hierarchy. Rendered
 * as a one-click chip that applies an OR-combined `class_description` /
 * `sub_class_description` filter (and can be toggled back off).
 */
export interface CollaboratorCategorySuggestion {
  level: 'class' | 'subclass';
  names: string[];
  label: string;
  filters: string;
  tag: CollaboratorFilterTag;
  result_count?: number;
}

export interface SemanticSearchResponse {
  success: boolean;
  query?: string;
  products?: ProductItem[];
  facets?: ProductDisplayData['facets'];
  total_count?: number;
  boosts_applied?: CollaboratorBoosts;
  category_suggestions?: CollaboratorCategorySuggestion[];
  score_algo?: string;
  error?: string;
}

export interface SemanticSearchOptions {
  industryTop10Codes?: string[];
  industryName?: string;
  /**
   * Industries currently being filtered — the backend tags each product with a
   * `🏭 {Industry}` chip for every one of these it is marked for.
   */
  industryFilterNames?: string[];
}

export async function productSemanticSearch(
  query: string,
  top = 24,
  filters?: string,
  boosts?: CollaboratorBoosts,
  options?: SemanticSearchOptions
): Promise<SemanticSearchResponse> {
  const baseUrl = getApiBaseUrl();
  const oidcService = OidcAuthService.getInstance();
  const token = await oidcService.getAccessToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`${baseUrl}/product/semantic-search`, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: JSON.stringify({
      query,
      top,
      filters,
      boosts,
      industry_top_10_codes: options?.industryTop10Codes,
      industry_name: options?.industryName,
      industry_filter_names: options?.industryFilterNames,
    }),
  });

  let payload: unknown;
  try {
    payload = await res.json();
  } catch {
    payload = null;
  }

  const data = (payload ?? {}) as SemanticSearchResponse & { message?: string };

  if (!res.ok) {
    return {
      success: false,
      error:
        data.error ||
        data.message ||
        `Search request failed (${res.status})`,
    };
  }

  return data;
}

export interface CategorySuggestResponse {
  success: boolean;
  query?: string;
  category_suggestions?: CollaboratorCategorySuggestion[];
  error?: string;
}

/**
 * Quick, products-free category-filter suggestions for a query (pure semantic
 * embedding match against the hierarchy). Loaded asynchronously so it never
 * blocks the search; pass an AbortSignal to cancel when a new search starts.
 */
export async function fetchCategorySuggestions(
  query: string,
  filters?: string,
  signal?: AbortSignal
): Promise<CategorySuggestResponse> {
  const baseUrl = getApiBaseUrl();
  const oidcService = OidcAuthService.getInstance();
  const token = await oidcService.getAccessToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`${baseUrl}/product/collaborator/category-suggest`, {
    method: 'POST',
    headers,
    credentials: 'include',
    signal,
    body: JSON.stringify({ query, filters }),
  });

  let payload: unknown;
  try {
    payload = await res.json();
  } catch {
    payload = null;
  }
  const data = (payload ?? {}) as CategorySuggestResponse & { message?: string };
  if (!res.ok) {
    return { success: false, error: data.error || data.message || `Suggest failed (${res.status})` };
  }
  return data;
}

export async function collaboratorChatFilter(
  utterance: string,
  currentBoosts?: CollaboratorBoosts,
  context?: CollaboratorChatContext
): Promise<CollaboratorFilterResponse> {
  const baseUrl = getApiBaseUrl();
  const oidcService = OidcAuthService.getInstance();
  const token = await oidcService.getAccessToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`${baseUrl}/product/collaborator/filter`, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: JSON.stringify({ utterance, current_boosts: currentBoosts, context }),
  });

  let payload: unknown;
  try {
    payload = await res.json();
  } catch {
    payload = null;
  }

  const data = (payload ?? {}) as CollaboratorFilterResponse & { message?: string };

  if (!res.ok) {
    return {
      success: false,
      error: data.error || data.message || `Filter request failed (${res.status})`,
    };
  }

  return data;
}

export interface ResolveConstraintResponse {
  success: boolean;
  matched: boolean;
  term?: string;
  field?: string;
  value?: unknown;
  label?: string;
  confidence?: number;
  odata?: string;
  tag?: CollaboratorFilterTag | null;
  error?: string;
}

/**
 * Resolve a single free-text constraint to a real catalog filter via the
 * threshold-gated backend resolver. `matched: false` means no addable filter.
 */
export async function resolveCollaboratorConstraint(
  term: string
): Promise<ResolveConstraintResponse> {
  const baseUrl = getApiBaseUrl();
  const oidcService = OidcAuthService.getInstance();
  const token = await oidcService.getAccessToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`${baseUrl}/product/collaborator/resolve-constraint`, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: JSON.stringify({ term }),
  });

  let payload: unknown;
  try {
    payload = await res.json();
  } catch {
    payload = null;
  }
  const data = (payload ?? {}) as ResolveConstraintResponse & { message?: string };
  if (!res.ok) {
    return {
      success: false,
      matched: false,
      error: data.error || data.message || `Resolve failed (${res.status})`,
    };
  }
  return data;
}

export async function streamCollaboratorInquire(
  query: string,
  products: ProductItem[],
  onEvent: (event: Record<string, unknown>) => void,
  options?: {
    fastMode?: boolean;
    signal?: AbortSignal;
    constraints?: string[];
    wants?: string[];
    filterLabels?: string[];
    /** Conversation so far (prior Inquire breakdown + follow-ups), focus-scoped. */
    history?: Array<{ role: 'user' | 'agent'; content: string }>;
    /** Additional user text on a follow-up turn (e.g. "which is best for outdoors?"). */
    followUp?: string;
    /** Inquiry-graph snapshot: totals + current focus, so the agent knows where it is. */
    graphSummary?: {
      total_nodes?: number;
      focus_label?: string;
      focus_num_items?: number;
    };
  }
): Promise<void> {
  const baseUrl = getApiBaseUrl();
  const oidcService = OidcAuthService.getInstance();
  const token = await oidcService.getAccessToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
  };
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`${baseUrl}/product/collaborator/inquire/stream`, {
    method: 'POST',
    headers,
    credentials: 'include',
    signal: options?.signal,
    body: JSON.stringify({
      query,
      products,
      fastMode: options?.fastMode ?? true,
      constraints: options?.constraints,
      wants: options?.wants,
      filterLabels: options?.filterLabels,
      history: options?.history,
      followUp: options?.followUp,
      graphSummary: options?.graphSummary,
    }),
  });

  if (!res.ok || !res.body) {
    throw new Error(`Inquire failed: ${res.status}`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';
    for (const line of lines) {
      if (!line.startsWith('data: ')) continue;
      const data = line.slice(6).trim();
      if (data === '[DONE]') return;
      try {
        onEvent(JSON.parse(data));
      } catch {
        /* ignore partial */
      }
    }
  }
}

export interface CollaboratorProspectBootstrapResponse {
  success: boolean;
  prospect?: CollaboratorProspect;
  approvedIndustry?: string;
  industryId?: string;
  industryTop10Codes?: string[];
  filters?: string;
  tags?: CollaboratorFilterTag[];
  boosts?: CollaboratorBoosts;
  starterQuery?: string;
  agentMessage?: string;
  lanes?: CollaboratorGarmentLane[];
  error?: string;
}

export async function collaboratorProspectBootstrap(
  prospect: CollaboratorProspect | Partial<CollaboratorProspect> | { text: string }
): Promise<CollaboratorProspectBootstrapResponse> {
  const baseUrl = getApiBaseUrl();
  const oidcService = OidcAuthService.getInstance();
  const token = await oidcService.getAccessToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`${baseUrl}/product/collaborator/prospect`, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: JSON.stringify(prospect),
  });

  let payload: unknown;
  try {
    payload = await res.json();
  } catch {
    payload = null;
  }

  const raw = (payload ?? {}) as Record<string, unknown> & { message?: string };

  if (!res.ok) {
    return {
      success: false,
      error:
        (raw.error as string) ||
        raw.message ||
        `Prospect bootstrap failed (${res.status})`,
    };
  }

  return normalizeProspectBootstrap(raw);
}

function normalizeProspectBootstrap(raw: Record<string, unknown>): CollaboratorProspectBootstrapResponse {
  const lanesRaw = (raw.lanes as Record<string, unknown>[]) || [];
  const lanes: CollaboratorGarmentLane[] = lanesRaw.map((lane) => ({
    id: String(lane.id || ''),
    label: String(lane.label || ''),
    searchQuery: String(lane.searchQuery || lane.search_query || ''),
    status: (lane.status as CollaboratorGarmentLane['status']) || 'pending',
    rationale: lane.rationale ? String(lane.rationale) : undefined,
  }));

  return {
    success: Boolean(raw.success),
    error: raw.error ? String(raw.error) : undefined,
    prospect: raw.prospect as CollaboratorProspect | undefined,
    approvedIndustry: String(raw.approvedIndustry || raw.approved_industry || ''),
    industryId: raw.industryId ? String(raw.industryId) : undefined,
    industryTop10Codes: (raw.industryTop10Codes || raw.industry_top_10_codes) as
      | string[]
      | undefined,
    filters: raw.filters ? String(raw.filters) : undefined,
    tags: Array.isArray(raw.tags)
      ? (raw.tags as CollaboratorFilterTag[])
      : undefined,
    boosts: raw.boosts as CollaboratorProspectBootstrapResponse['boosts'],
    starterQuery: String(raw.starterQuery || raw.starter_query || ''),
    agentMessage: String(raw.agentMessage || raw.reply || ''),
    lanes,
  };
}
